import React, {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  useCallback,
} from 'react'
import { StepTitle } from '@components/layout/Form'
import PageContent from '@components/layout/PageContent'
import {
  Alert,
  Box,
  Button,
  ColorType,
  Flex,
  InputGroup,
  Link,
  RadioGroup,
  Text,
} from '@clear/design-system'
import { useJsApiLoader } from '@react-google-maps/api'
import { GeoLocate } from '@components/shared/GeoLocate'
import { Map } from '@components/storybook'
import {
  getNearbyEnrollmentLocations,
  EnrollmentLocation,
  determineMapZoomLevel,
} from '@utils/getNearbyEnrollmentLocations'
import { useLocationsContext } from '@contexts/LocationsContext'
import { getXCLEARCorrelationInfo } from '@clear-denver/tpc-openapi-typescript/build/utils'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import router from 'next/router'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { parse, format } from 'date-fns'
import { FeatureCard } from '@components/storybook/FeatureCard'

export default function ModifyAppointment() {
  const flowContainerRef = useRef(null)
  const { enrollmentLocations } = useLocationsContext()
  const [parsedEnrollmentId, setParsedEnrollmentId] = useState<string | null>(
    null
  )
  const [parsedTerritoryId, setParsedTerritoryId] = useState<string | null>(
    null
  )
  const [showLocationSelector, setShowLocationSelector] =
    useState<boolean>(false)
  const [searchLocation, setSearchLocation] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [mapSearchError, setMapSearchError] = useState(false)
  const [isSearchComplete, setIsSearchComplete] = useState<boolean>(false)
  const [locationsWithinRange, setLocationsWithinRange] = useState<
    EnrollmentLocation[]
  >([])
  const [hideButtons, setHideButtons] = useState<boolean>(false)
  const [enrollmentLocationId, setEnrollmentLocationId] = useState<string>('')
  const [showSchedulerFlow, setShowSchedulerFlow] = useState<boolean>(false)
  const { apptInfo, setApptInfo } = useEnrollmentContext()

  function parseBrokenQuery(rawUrl: string) {
    const clean = rawUrl.substring(1).replace(/\?(?=\w+=)/g, '&')
    const params = new URLSearchParams(clean)
    return Object.fromEntries(params.entries())
  }

  const updateSearchBar = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e?.target?.value)
  }

  function findByServiceTerritory(locations, serviceTerritory) {
    if (!locations || locations.length === 0) {
      return null
    }
    return locations.find((obj) => obj.serviceTerritoryId === serviceTerritory)
  }

  const location = findByServiceTerritory(
    enrollmentLocations,
    parsedTerritoryId
  )

  const currentLocation = `${location?.name} - ${location?.address1} ${
    location?.address2 ?? ''
  } ${location?.city}, ${location?.state}`

  const handleLocationSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.value) {
      setParsedTerritoryId(e.target.value)
      setShowLocationSelector(false)
      setShowSchedulerFlow(true)
    }
  }

  const geoLocateSuccessHandler = (position: GeolocationPosition) => {
    handleSearch(`${position.coords.latitude},${position.coords.longitude}`)
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      handleSearch(searchInput)
    }
  }

  const handleKeepServiceTerritory = () => {
    setShowSchedulerFlow(true)
    setHideButtons(true)
  }

  const handleSelectNewServiceTerritory = () => {
    setShowLocationSelector(true)
    setHideButtons(true)
    setParsedTerritoryId('')
  }

  const isEnrollmentLocationAdded = enrollmentLocationId.length > 0
  const errors = isEnrollmentLocationAdded
    ? { enrollmentLocationId: 'Please select a valid enrollment location.' }
    : {}

  const handleSearch = useCallback(
    async (searchCriteria: string) => {
      setIsSearchComplete(false)
      setMapSearchError(false)
      setLocationsWithinRange([])
      const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
      const baseMetaData = {
        kpi: KPI.USER_INTERACTION,
        xCLEARCorrelationInfo,
        url: router.route,
      }
      setEnrollmentLocationId('')
      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Get nearby enrollment locations begin',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.GET_NEARBY_ENROLLMENT_LOCATIONS_BEGIN,
        },
      })
      try {
        const { enrollmentLocationsNearSearch } =
          await getNearbyEnrollmentLocations(
            enrollmentLocations,
            searchInput || searchCriteria
          )
        dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
          payload: {
            message: 'Nearby enrollment locations fetched successfully',
          },
          metadata: {
            ...baseMetaData,
            enrollmentLocationsNearSearch,
            event: DiscreteEvent.GET_NEARBY_ENROLLMENT_LOCATIONS_SUCCESSFUL,
          },
        })
        const sortedEnrollmentLocationsNearSearch =
          enrollmentLocationsNearSearch.sort((a, b) => a.distance - b.distance)
        setLocationsWithinRange(sortedEnrollmentLocationsNearSearch)
        setSearchLocation(
          enrollmentLocationsNearSearch[0]?.location || searchInput
        )
        setSearchInput('')
        setIsSearchComplete(true)
      } catch (e) {
        dispatchCustomEvent(Enrollment.LogEntryLevelEnum.ERROR, {
          payload: {
            message: e?.message,
            stack: e?.stack,
          },
          metadata: {
            event: DiscreteEvent.GET_NEARBY_ENROLLMENT_LOCATIONS_FAILURE,
            ...baseMetaData,
          },
        })
        setMapSearchError(true)
        setSearchInput('')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setLocationsWithinRange,
      setSearchLocation,
      setMapSearchError,
      searchInput,
      enrollmentLocations,
    ]
  )

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
  })

  useEffect(() => {
    const params = parseBrokenQuery(window.location.search)
    setParsedEnrollmentId(params.enrollmentId ?? null)
    setParsedTerritoryId(params.territoryId ?? null)
  }, [])

  useEffect(() => {
    const inputVariables = [
      {
        name: 'enrollmentId',
        type: 'String',
        value: parsedEnrollmentId,
      },
      {
        name: 'territoryId',
        type: 'String',
        value: parsedTerritoryId,
      },
    ]
    if (parsedTerritoryId && parsedEnrollmentId && showSchedulerFlow) {
      const script = document.createElement('script')
      script.src = process.env.NEXT_PUBLIC_SF_LIGHTNING_URL
      script.async = true
      script.onload = () => {
        window.$Lightning.use(
          'c:FlowHostApp',
          function () {
            window.$Lightning.createComponent(
              'c:FlowHost',
              {
                flowApiName: process.env.NEXT_PUBLIC_SF_FLOW_NAME,
                inputVariables,
              },
              flowContainerRef.current
            )
          },
          process.env.NEXT_PUBLIC_SF_ORG_BASE_URL
        )
        const REQUIRED = ['convertedStartTime', 'locationAddress']
        let handled = false

        window.addEventListener('message', function (e) {
          if (handled) {
            return
          }
          const out = e?.data?.output
          if (!Array.isArray(out)) {
            return
          }
          const outputs = Object.fromEntries(out.map((v) => [v.name, v.value]))
          const hasAll = REQUIRED.every(
            (k) =>
              outputs[k] !== undefined &&
              outputs[k] !== null &&
              String(outputs[k].trim() !== '')
          )
          if (!hasAll) {
            return
          }
          handled = true
          const rawTime = outputs.convertedStartTime
          const locationAddress = outputs.locationAddress
          const parsedTime = parse(rawTime, 'MM/dd/yyyy, hh:mm a', new Date())
          let convertedStartTime = format(
            parsedTime,
            'MMMM do, yyyy, h:mmaaa'
          ).toLowerCase()
          convertedStartTime =
            convertedStartTime.charAt(0).toUpperCase() +
            convertedStartTime.slice(1)
          setApptInfo({
            ...apptInfo,
            locationAddress,
            convertedStartTime,
          })
          setShowSchedulerFlow(false)
        })
      }
      const styleTag = document.createElement('style')
      styleTag.textContent = `.slds-visual-picker_vertical .slds-visual-picker__figure {
          height: inherit !important;}
          .runtime_appointmentbookingFlowLocation .slds-visual-picker_vertical {
          display: inline-flex !important;
          }`
      document.head.appendChild(styleTag)
      document.body.appendChild(script)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedEnrollmentId, parsedTerritoryId, showSchedulerFlow])

  return (
    <PageContent width="800px">
      {!apptInfo ? (
        <>
          <StepTitle>Book Your Appointment</StepTitle>
          <Text
            as="p"
            className="text-dhs-blue"
            my={5}
            fontSize="18px"
            fontWeight={400}
          >
            Walk-ins are always welcome.
          </Text>
          <Alert
            mb={5}
            defaultIcon
            backgroundColor={'dhsBlue' as ColorType}
            color="neutralsWhite"
            text="Save time and book an appointment"
          />
        </>
      ) : (
        <>
          <StepTitle mb={6}>
            {apptInfo?.convertedStartTime &&
              'Your appointment has been booked!'}
          </StepTitle>
          <Alert
            mb={6}
            defaultIcon
            text="Starting May 7, 2025, please ensure you have your REAL ID or other Acceptable Form of ID (for example, a passport) prior to traveling or you may encounter delays at the (airport) checkpoint."
          />
        </>
      )}
      {!hideButtons && currentLocation ? (
        <>
          Your current enrollment location is {currentLocation}. Do you want to:
          <Flex
            flexDirection={['column', 'row']}
            justifyContent="space-between"
            mt={5}
          >
            <Button
              text="Book at Current Location"
              onClick={() => handleKeepServiceTerritory()}
              mb={[5, null]}
            />
            <Button
              text="Book at New Location"
              onClick={() => handleSelectNewServiceTerritory()}
            />
          </Flex>
        </>
      ) : null}
      {showLocationSelector && (
        <>
          <Flex
            alignItems="center"
            justifyContent="center"
            mb={7}
            width="full"
            flexDirection={['column', 'row']}
          >
            <InputGroup
              icon="search"
              onKeyDown={(e) => {
                handleKeyDown(e)
              }}
              variant="default"
              iconClick={() => handleSearch(searchInput)}
              onChange={updateSearchBar}
              value={searchInput}
              placeholder="Zip Code / City / Airport Code"
              autoComplete="off"
              data-cy="searchBar"
            />
            <GeoLocate onSuccessHandler={geoLocateSuccessHandler} />
          </Flex>
          {mapSearchError ? (
            <Box as="p" textAlign="center" color="utilityDarkErrorRed">
              Sorry, that's not a valid search. Please try again.
            </Box>
          ) : null}
          <Box as="p" textAlign="center" color="utilityDarkErrorRed">
            {locationsWithinRange.length === 0
              ? errors?.enrollmentLocationId?.length
              : null}
          </Box>
          {locationsWithinRange?.length ? (
            <Flex flexDirection={['column-reverse', 'row']}>
              <Box
                width={['full', '55%']}
                mr={4}
                height="500px"
                overflowY="scroll"
                pr={4}
              >
                <RadioGroup
                  name="searchResult"
                  options={locationsWithinRange.map(
                    (location: EnrollmentLocation) => ({
                      key: location?.serviceTerritoryId,
                      label: location?.name,
                      subLabel: (
                        <>
                          <div className="h-1/2" />
                          <Text
                            fontFamily="Inter-SemiBold"
                            variant="display"
                            fontSize="16px"
                          >
                            {location?.distance} miles
                          </Text>
                          <br />
                          <div className="h-1/2" />
                          <Text variant="body" fontSize="14px">
                            {location?.address}
                          </Text>
                          {location?.importantInfo && (
                            <Text color="utilityDarkErrorRed">
                              {location?.importantInfo}
                            </Text>
                          )}
                        </>
                      ),
                      value: location.serviceTerritoryId,
                    })
                  )}
                  onChange={handleLocationSelected}
                  containerProps={{
                    flexDirection: 'column',
                    flexShrink: 0,
                    alignItems: 'start',
                    display: 'flex-end',
                  }}
                />
              </Box>
              {isLoaded && searchLocation ? (
                <Box
                  width={['full', '50%']}
                  height={['400px', '500px']}
                  mb={[6, 0]}
                >
                  <Map
                    center={searchLocation}
                    zoom={determineMapZoomLevel(locationsWithinRange)}
                    locationMarkers={locationsWithinRange}
                    full
                  />
                </Box>
              ) : (
                <>Loading google maps</>
              )}
            </Flex>
          ) : null}
          {!locationsWithinRange.length &&
          searchLocation &&
          !mapSearchError &&
          isSearchComplete ? (
            <Flex alignItems="start" ml={4} mt={3}>
              <Box as="img" src="/map-pin-not-found.svg" alt="pin-not-found" />
              <Box ml={3}>
                <Text fontSize={4} display="block" mb={1}>
                  No locations found
                </Text>
                <Text fontSize={1}>
                  Please adjust your search for better results.
                </Text>
              </Box>
            </Flex>
          ) : null}
          {locationsWithinRange && locationsWithinRange[0]?.distance > 40 && (
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              my={6}
            >
              <Text fontSize={4} display="block" mb={1}>
                Need a closer location?
              </Text>
              <Text fontSize={2}>
                Visit the{' '}
                <Link
                  target="_blank"
                  href="https://www.tsa.gov/precheck/enrollment-centers"
                >
                  TSA website
                </Link>{' '}
                for more enrollment location options.
              </Text>
            </Flex>
          )}
        </>
      )}
      {apptInfo && (
        <>
          <Text as="p" variant="body" textAlign="left" my={6}>
            Please visit any TSA PreCheck<sup>®</sup> through CLEAR enrollment
            location with your required documents to complete your application.
            For the best experience, we encourage you to book an appointment but
            we also accept walk-ins.
            <br />
            <br />
            Once completed, your application will be submitted for review. If
            approved, most applications receive their Known Traveler number
            (KTN) in 3-5 days (review periods can take up to 60 days).
          </Text>
          <Text as="p" variant="body" textAlign="left" mb={6}>
            If you are a Military Survivor Family member or military spouse,
            bring your TAPS letter or unexpired U.S. Military Dependent’s Card
            to your in-person enrollment for a discount.
          </Text>
          <Flex
            flexDirection="column"
            width="full"
            justifyContent="space-between"
            mb={6}
          >
            {apptInfo?.convertedStartTime && (
              <Text fontFamily="Inter-SemiBold" mb={3}>
                Appointment Details:
              </Text>
            )}
            {apptInfo?.convertedStartTime && apptInfo?.locationAddress && (
              <>
                <Text fontFamily="Inter-SemiBold">Location</Text>
                <Text mb={3}>{apptInfo?.locationAddress}</Text>
                <Text fontFamily="Inter-SemiBold">Details</Text>
                <Text mb={3}>{apptInfo?.convertedStartTime}</Text>
              </>
            )}
          </Flex>

          <Text fontFamily="Inter-SemiBold" mb={5}>
            Travel is about to get so much easier.
          </Text>
          <Flex
            flexDirection={['column', 'row']}
            my={5}
            justifyContent="space-between"
            width="100%"
          >
            <FeatureCard
              icon={<img src="/feature1.svg" alt="feature1" />}
              fontWeight={0}
              borderWidth="2px"
              borderColor="trueBlue"
              borderRadius="8px"
              description="No need to remove shoes, laptops, 3-1-1 liquids, belts, or light jackets"
            />
            <FeatureCard
              icon={<img src="/feature2.svg" alt="feature2" />}
              fontWeight={0}
              borderColor="trueBlue"
              borderWidth="2px"
              borderRadius="8px"
              description="Kids 17 and under join for free when booked on the same reservation"
            />
            <FeatureCard
              icon={<img src="/feature3.svg" alt="feature3" />}
              fontWeight={0}
              borderColor="trueBlue"
              borderWidth="2px"
              borderRadius="8px"
              description="Available with 90+ airlines at 200+ airports nationwide"
            />
          </Flex>
        </>
      )}

      <div
        ref={flowContainerRef}
        id="flowContainer"
        style={{ display: apptInfo ? 'none' : 'block' }}
      />
    </PageContent>
  )
}
